import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import SEO, { PAGE_SEO } from "./SEO";
import {
  Target,
  Settings,
  TrendingUp,
  User,
  ArrowRight,
  Cloud,
  Zap,
  BarChart3,
  Package,
  ChevronDown,
  Smartphone,
  Clock,
} from "lucide-react";
import { UserProfile, WeatherConditions } from "../types";
import { getCurrentWeather, getWeatherRecommendations, getUserLocation } from "../utils/weather";
import {
  generatePracticeRecommendations,
  getProgressHistory,
  generateProgressInsights,
} from "../utils/progress";
import { 
  getAvailableTips, 
  getShareCount,
} from "../utils/proTips";
import { getFirstTouch } from "../utils/firstTouch";

interface DashboardProps {
  userProfile: UserProfile | null;
  authUser: any | null;
  onActionRequiringInfo: (action: string) => void;
  onOpenAuthModal: (mode: "signin" | "signup") => void;
  onOpenContactForm?: () => void;
  onOpenOnboarding?: () => void;
}

const Dashboard: React.FC<DashboardProps> = ({ userProfile, authUser, onActionRequiringInfo, onOpenAuthModal, onOpenContactForm, onOpenOnboarding }) => {
  const navigate = useNavigate();
  const [currentPhoneScreen, setCurrentPhoneScreen] = useState(0);
  
  // Handle feature tile clicks - navigate if logged in, show signup if not
  const handleFeatureClick = (path: string) => {
    if (authUser) {
      navigate(path);
    } else {
      onOpenAuthModal("signup");
    }
  };
  
  // Phone mockup screenshots for carousel
  const phoneScreenshots = [
    { src: '/Images/App Screens for Home Screen/IMG_7964.png', alt: 'Profile Page' },
    { src: '/Images/App Screens for Home Screen/IMG_7965.png', alt: 'My Golf Bag - Club List' },
    { src: '/Images/App Screens for Home Screen/IMG_7966.png', alt: 'My Golf Bag - With Tabs' },
  ];
  const [, setWeather] = useState<WeatherConditions | null>(null);
  const [, setWeatherRecommendations] = useState<string[]>([]);
  const [, setPracticeRecommendations] = useState<any[]>([]);
  const [, setProgressInsights] = useState<string[]>([]);
  const [, setProTips] = useState<any[]>([]);
  const [, setShareCount] = useState<number>(0);
  const [currentTipIndex, setCurrentTipIndex] = useState<number>(0);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isLoadingLocation, setIsLoadingLocation] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showWelcomeToBagModal, setShowWelcomeToBagModal] = useState<boolean>(false);

  useEffect(() => {
    // Do not invent a default city. Without coordinates this returns unavailable.
    getCurrentWeather().then((weatherData) => {
      setWeather(weatherData);
      const weatherRecs = getWeatherRecommendations(weatherData);
      setWeatherRecommendations(Array.isArray(weatherRecs) ? weatherRecs : []);
    });

    // Load practice recommendations and progress insights
    if (userProfile) {
      const recommendations = generatePracticeRecommendations(userProfile);
      setPracticeRecommendations(Array.isArray(recommendations) ? recommendations.slice(0, 3) : []); // Show top 3

      const progressHistory = getProgressHistory();
      const insights = generateProgressInsights(userProfile, progressHistory);
      setProgressInsights(Array.isArray(insights) ? insights : []);
    }

    // Load pro tips and share count
    const tips = getAvailableTips();
    const unlockedTips = tips.filter(tip => tip.isUnlocked);
    setProTips(unlockedTips);
    setShareCount(getShareCount());
    
    // Reset tip index if it's out of bounds
    if (currentTipIndex >= unlockedTips.length) {
      setCurrentTipIndex(0);
    }
  }, [userProfile, currentTipIndex]);


  // Check if we should show the welcome modal (first-time users after onboarding)
  useEffect(() => {
    const userId = authUser?.id;
    if (!userId) return;

    const hasVisited = localStorage.getItem(`hasVisitedMyGolfBag_${userId}`);

    // Allow fallback based on localStorage backups in case DB read lags after email confirmation
    const hasLocalBackup =
      !!localStorage.getItem(`PERSISTENT_PROFILE_${userId}`) ||
      !!localStorage.getItem(`quizBackup_${userId}`) ||
      !!localStorage.getItem(`onboarding_${userId}`);

    const onboardingComplete = userProfile?.hasCompletedOnboarding === true;
    const shouldShow = (onboardingComplete || hasLocalBackup) && !hasVisited;

    if (shouldShow) {
      const timer = setTimeout(() => setShowWelcomeToBagModal(true), 250);
      return () => clearTimeout(timer);
    }
  }, [authUser?.id, userProfile?.id, userProfile?.hasCompletedOnboarding]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleGetLocation = async () => {
    setIsLoadingLocation(true);
    try {
      const location = await getUserLocation();
      const weatherData = await getCurrentWeather(location.lat, location.lon);
      setWeather(weatherData);
      setWeatherRecommendations(getWeatherRecommendations(weatherData));
    } catch (error) {
      console.error('Failed to get location or weather:', error);
      // You could show a toast or alert here
    } finally {
      setIsLoadingLocation(false);
    }
  };
  // TWO-PILLAR SYSTEM - Focus on main hubs!
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const pillars = [
    {
      title: "My Golf Bag",
      description: "Your clubs, distances, and shot advice - everything you need on the course",
      icon: BarChart3,
      path: "/my-golf-bag",
      color: "bg-green-500",
      features: ["View your clubs", "Track distances", "Get shot advice"]
    },
    {
      title: "Club Optimizer",
      description: "Analyze your setup, get recommendations, and optimize your equipment",
      icon: Settings,
      path: "/club-optimizer",
      color: "bg-purple-500",
      features: ["Equipment fitting", "Gapping analysis", "AI recommendations"]
    },
  ];

  // Secondary tools - less prominent
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const secondaryTools = [
    {
      title: "Swing Analyzer",
      description: "Improve your swing with AI analysis",
      icon: TrendingUp,
      path: "/club-optimizer?tab=swing-tips",
      color: "bg-orange-500",
    },
    {
      title: "Ball Matching",
      description: "Find the perfect golf ball for your game",
      icon: Zap,
      path: "/club-optimizer?tab=ball-match",
      color: "bg-yellow-500",
    },
  ];


  // Phone screenshot carousel rotation
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentPhoneScreen((prev) => (prev + 1) % phoneScreenshots.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [phoneScreenshots.length]);

  return (
    <>
      <SEO {...PAGE_SEO.home} url="/" />
    <div className="bg-white w-full overflow-x-hidden m-0 p-0">
      {/* Full-Width Hero Banner with Background Image - Split Layout */}
      <div className="relative w-full min-h-[85vh] md:min-h-[90vh] flex items-center overflow-hidden">
        {/* Background Image - Full Width */}
        <div 
          className="absolute inset-0 w-full h-full bg-cover bg-center"
          style={{
            backgroundImage: `url('/Images/pexels-pixabay-164250.jpg')`,
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/70 to-black/50"></div>
        </div>
                  
        {/* Animated Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-green-500/20 rounded-full mix-blend-overlay filter blur-3xl opacity-40 animate-blob"></div>
          <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-emerald-500/20 rounded-full mix-blend-overlay filter blur-3xl opacity-40 animate-blob animation-delay-2000"></div>
        </div>
                  
        {/* Content Container - Split Layout */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 py-8 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Copy */}
            <div className="text-left">
              {getFirstTouch().partnerCode && (
                <div className="mb-4 px-4 py-3 bg-white/15 backdrop-blur-sm border border-white/30 rounded-xl text-white text-sm md:text-base">
                  You arrived through a ClubIQ instructor partner. Create an account, add your bag, and you will see your own club distances. There is no extra charge for using a partner link.
                </div>
              )}
              {/* AI Badge */}
              <div className="mb-6 animate-fade-in">
                <div className="inline-block px-3 py-1 bg-green-500/90 backdrop-blur-sm text-white rounded-full text-xs font-bold uppercase tracking-wider shadow-xl">
                  AI-Powered Golf Intelligence
                </div>
              </div>
                
            {/* Hero Typography - New Copy */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-6 leading-[1.1] tracking-tight drop-shadow-2xl animate-slide-up">
                Your AI Club Fitter<br />
                <span className="bg-gradient-to-r from-green-400 via-emerald-400 to-green-300 bg-clip-text text-transparent">
                  and Caddie
                </span>
              </h1>
            
              {/* Subheadline - Clearer Value Prop */}
              <p className="text-lg md:text-xl text-white/90 max-w-xl leading-relaxed mb-8 font-medium drop-shadow-lg animate-fade-in animation-delay-200">
                Get personalized club recommendations based on your swing, build, and skill level. Built to match you with the exact clubs that fit your game.
              </p>

              {/* No Hardware Badge */}
              <div className="flex items-center gap-2 mb-8 animate-fade-in animation-delay-300">
                <Smartphone className="w-5 h-5 text-green-400" />
                <span className="text-white/80 text-sm font-medium">No hardware required. Just your phone and your bag.</span>
              </div>
            
              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 animate-fade-in animation-delay-400">
                {authUser ? (
                  <>
                    <Link
                      to="/profile"
                      className="group relative px-8 py-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl text-base font-semibold hover:from-green-700 hover:to-emerald-700 transition-all duration-300 shadow-2xl shadow-green-500/40 hover:shadow-green-400/60 hover:scale-105 inline-flex items-center justify-center"
                    >
                      Go to My Profile
                      <svg className="w-4 h-4 ml-2 inline-block group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </Link>
                    <Link
                      to="/my-golf-bag"
                      className="px-8 py-4 text-white bg-white/10 backdrop-blur-md border-2 border-white/30 rounded-xl text-base font-semibold hover:bg-white/20 hover:border-white/50 transition-all duration-300 hover:scale-105 text-center"
                    >
                      View My Bag
                    </Link>
                  </>
                ) : (
                  <>
                    <button
                      onClick={() => onOpenAuthModal("signup")}
                      className="group relative px-8 py-4 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl text-base font-semibold hover:from-green-700 hover:to-emerald-700 transition-all duration-300 shadow-2xl shadow-green-500/40 hover:shadow-green-400/60 hover:scale-105"
                    >
                      Get Started Free
                      <svg className="w-4 h-4 ml-2 inline-block group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </button>
                    <button
                      onClick={() => onOpenAuthModal("signin")}
                      className="px-8 py-4 text-white bg-white/10 backdrop-blur-md border-2 border-white/30 rounded-xl text-base font-semibold hover:bg-white/20 hover:border-white/50 transition-all duration-300 hover:scale-105"
                    >
                      Sign In
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Right Column - Phone Mockup with Rotating Screenshots */}
            <div className="hidden lg:flex justify-center items-center animate-fade-in animation-delay-500">
              <div className="relative">
                {/* Phone Frame */}
                <div className="relative w-[280px] h-[580px] bg-gray-900 rounded-[3rem] p-2 shadow-2xl shadow-black/50 border-4 border-gray-800 z-10">
                  {/* Screen */}
                  <div className="w-full h-full bg-white rounded-[2.5rem] overflow-hidden relative">
                    {/* Notch */}
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-7 bg-gray-900 rounded-b-2xl z-10"></div>
                    {/* Screenshot Carousel with Fallback */}
                    <div className="relative w-full h-full">
                      {/* Fallback - always visible as background */}
                      <div className="absolute inset-0 w-full h-full bg-gradient-to-b from-green-50 to-white flex flex-col items-center justify-center p-6 pt-12">
                        <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl mb-4 flex items-center justify-center shadow-lg">
                          <BarChart3 className="w-7 h-7 text-white" />
                        </div>
                        <p className="text-gray-900 font-bold text-xl mb-1">My Golf Bag</p>
                        <p className="text-gray-500 text-sm text-center mb-6">Your clubs, distances, and AI recommendations</p>
                        <div className="space-y-3 w-full px-2">
                          <div className="bg-white rounded-xl p-4 shadow-md border border-gray-100 flex justify-between items-center">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-700 font-bold text-sm">1</div>
                              <span className="font-semibold text-gray-800">Driver</span>
                            </div>
                            <span className="text-green-600 font-bold text-lg">300y</span>
                          </div>
                          <div className="bg-white rounded-xl p-4 shadow-md border border-gray-100 flex justify-between items-center">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-700 font-bold text-sm">2</div>
                              <span className="font-semibold text-gray-800">7 Iron</span>
                            </div>
                            <span className="text-green-600 font-bold text-lg">165y</span>
                          </div>
                          <div className="bg-white rounded-xl p-4 shadow-md border border-gray-100 flex justify-between items-center">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center text-green-700 font-bold text-sm">3</div>
                              <span className="font-semibold text-gray-800">PW</span>
                            </div>
                            <span className="text-green-600 font-bold text-lg">135y</span>
                          </div>
                        </div>
                      </div>
                      {/* Screenshots overlay on top when loaded */}
                      {phoneScreenshots.map((screen, index) => (
                        <img 
                          key={index}
                          src={screen.src} 
                          alt={screen.alt}
                          className={`absolute inset-0 w-full h-full object-cover object-top transition-opacity duration-700 z-10 ${
                            index === currentPhoneScreen ? 'opacity-100' : 'opacity-0'
                          }`}
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                          }}
                        />
                      ))}
                    </div>
                    {/* Carousel Dots */}
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5 z-20">
                      {phoneScreenshots.map((_, index) => (
                        <button
                          key={index}
                          onClick={() => setCurrentPhoneScreen(index)}
                          className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                            index === currentPhoneScreen 
                              ? 'w-4 bg-green-500' 
                              : 'bg-gray-300 hover:bg-gray-400'
                          }`}
                        />
                      ))}
                    </div>
                  </div>
                </div>
                {/* Floating Stats - Dynamic based on current screen */}
                {/* Screen 0: Profile - Show Handicap & Gapping Score */}
                {currentPhoneScreen === 0 && (
                  <>
                    <div className="absolute -left-16 top-24 bg-white/95 backdrop-blur-sm rounded-xl p-3 shadow-xl animate-float z-30">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                          <User className="w-4 h-4 text-purple-600" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Handicap</p>
                          <p className="text-sm font-bold text-gray-900">7 HCP</p>
                        </div>
                      </div>
                    </div>
                    <div className="absolute -right-12 bottom-32 bg-white/95 backdrop-blur-sm rounded-xl p-3 shadow-xl animate-float animation-delay-1000 z-30">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                          <Target className="w-4 h-4 text-blue-600" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Gapping Score</p>
                          <p className="text-sm font-bold text-gray-900">54/100</p>
                        </div>
                      </div>
                    </div>
                  </>
                )}
                {/* Screen 1: Club List - Show Driver & Iron distances */}
                {currentPhoneScreen === 1 && (
                  <>
                    <div className="absolute -left-16 top-24 bg-white/95 backdrop-blur-sm rounded-xl p-3 shadow-xl animate-float z-30">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                          <Zap className="w-4 h-4 text-green-600" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Driver</p>
                          <p className="text-sm font-bold text-gray-900">300 yards</p>
                        </div>
                      </div>
                    </div>
                    <div className="absolute -right-12 bottom-32 bg-white/95 backdrop-blur-sm rounded-xl p-3 shadow-xl animate-float animation-delay-1000 z-30">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                          <TrendingUp className="w-4 h-4 text-emerald-600" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">7-Iron</p>
                          <p className="text-sm font-bold text-gray-900">165 yards</p>
                        </div>
                      </div>
                    </div>
                  </>
                )}
                {/* Screen 2: Bag with tabs - Show Clubs in Bag & Shot Advisor */}
                {currentPhoneScreen === 2 && (
                  <>
                    <div className="absolute -left-16 top-24 bg-white/95 backdrop-blur-sm rounded-xl p-3 shadow-xl animate-float z-30">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                          <Package className="w-4 h-4 text-orange-600" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Clubs in Bag</p>
                          <p className="text-sm font-bold text-gray-900">14/14</p>
                        </div>
                      </div>
                    </div>
                    <div className="absolute -right-12 bottom-32 bg-white/95 backdrop-blur-sm rounded-xl p-3 shadow-xl animate-float animation-delay-1000 z-30">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-teal-100 rounded-lg flex items-center justify-center">
                          <Target className="w-4 h-4 text-teal-600" />
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Shot Advisor</p>
                          <p className="text-sm font-bold text-gray-900">Ready</p>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works Section */}
      <div className="w-full bg-white py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-6">
          {/* Section Header */}
          <div className="text-center mb-12">
            <div className="inline-block px-4 py-2 bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 rounded-full text-sm font-bold uppercase tracking-wider mb-4">
              Simple Setup
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900">
              How It Works
            </h2>
          </div>

          {/* 3 Steps */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="relative group">
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 h-full border border-green-100 hover:shadow-xl hover:shadow-green-500/10 transition-all duration-300">
                <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Package className="w-7 h-7 text-white" />
                </div>
                <div className="absolute top-6 right-6 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                  1
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Build Your Bag</h3>
                <p className="text-gray-600 leading-relaxed">
                  Add your clubs in 60 seconds. Driver, irons, wedges — we'll organize everything automatically.
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="relative group">
              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8 h-full border border-emerald-100 hover:shadow-xl hover:shadow-emerald-500/10 transition-all duration-300">
                <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <BarChart3 className="w-7 h-7 text-white" />
                </div>
                <div className="absolute top-6 right-6 w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                  2
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Dial In Distances</h3>
                <p className="text-gray-600 leading-relaxed">
                  Log shots at the range, import data, or use AI estimates based on your profile and swing speed.
                </p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="relative group">
              <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl p-8 h-full border border-teal-100 hover:shadow-xl hover:shadow-teal-500/10 transition-all duration-300">
                <div className="w-14 h-14 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <Target className="w-7 h-7 text-white" />
                </div>
                <div className="absolute top-6 right-6 w-8 h-8 bg-teal-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                  3
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Play Smarter</h3>
                <p className="text-gray-600 leading-relaxed">
                  Get real-time club recommendations on course. Know your true yardages, not guesses.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full bg-gradient-to-br from-gray-900 via-black to-gray-900 py-20 overflow-hidden relative">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-20 w-96 h-96 bg-green-500/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-emerald-500/5 rounded-full blur-3xl"></div>
        </div>
        <div className="relative z-10 max-w-5xl mx-auto px-6 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 leading-tight">
            What ClubIQ actually does
          </h2>
          <p className="text-gray-300 text-lg mb-12 max-w-3xl mx-auto">
            We do not publish customer quotes, star ratings, or named results we cannot verify.
            Here is the product, not invented golfers.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-left">
            <div className="bg-gray-800/60 border border-gray-700 rounded-2xl p-6">
              <h3 className="text-white font-semibold mb-2">Save your bag</h3>
              <p className="text-gray-300 text-sm">Add the clubs you own and keep them in one place.</p>
            </div>
            <div className="bg-gray-800/60 border border-gray-700 rounded-2xl p-6">
              <h3 className="text-white font-semibold mb-2">Enter your distances</h3>
              <p className="text-gray-300 text-sm">Use your numbers. ClubIQ does not invent yardages for you.</p>
            </div>
            <div className="bg-gray-800/60 border border-gray-700 rounded-2xl p-6">
              <h3 className="text-white font-semibold mb-2">Get recommendations</h3>
              <p className="text-gray-300 text-sm">AI suggestions are based on your profile and bag, not fake reviews.</p>
            </div>
          </div>
        </div>
      </div>
          
      {/* Bento Grid - Images Integrated with Features */}
      <div className="w-full bg-gradient-to-b from-white via-gray-50 to-white pt-12 md:pt-16 pb-0">
        <div className="w-full">
          {/* Section Header */}
          <div className="text-center mb-8 md:mb-12 px-4">
            <div className="inline-block px-4 py-2 bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 rounded-full text-sm font-bold uppercase tracking-wider mb-4 md:mb-6 shadow-md">
              Powerful Features
              </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4 md:mb-6">
              Every Feature.<br />
              <span className="bg-gradient-to-r from-green-600 to-emerald-600 bg-clip-text text-transparent">Every Detail.</span>
            </h2>
            </div>
          
          {/* Seamless Image Grid - No Gaps */}
          <div className="grid grid-cols-2 md:grid-cols-12 auto-rows-[180px] sm:auto-rows-[200px] md:auto-rows-[280px] w-full mb-0">
            {/* Large Feature Card with Image - spans 2 rows on ALL screen sizes */}
            <div className="col-span-2 md:col-span-6 row-span-2 group relative overflow-hidden bg-gradient-to-br from-green-500 to-emerald-600 p-4 sm:p-6 md:p-8">
              <div className="absolute inset-0 opacity-20">
                <img 
                  src="/Images/pexels-pixabay-164250.jpg" 
                  alt="Golf course"
                  className="w-full h-full object-cover mix-blend-overlay"
                />
              </div>
              <div className="relative z-10 h-full flex flex-col justify-between">
                <div>
                  <BarChart3 className="w-10 sm:w-12 md:w-16 h-10 sm:h-12 md:h-16 text-white mb-3 md:mb-6" />
                  <h3 className="text-xl sm:text-2xl md:text-4xl font-bold text-white mb-2 md:mb-4">Distance Intelligence</h3>
                  <p className="text-white/90 text-sm sm:text-base md:text-xl leading-relaxed">
                    Know your TRUE yardages — dispersion, carry, and rollout, adjusted for wind and elevation.
                  </p>
                </div>
                <div className="flex items-end gap-2 md:gap-4 mt-3 md:mt-8">
                  <div className="flex-1 h-14 sm:h-20 md:h-28 bg-white/10 backdrop-blur-sm rounded-lg md:rounded-2xl p-2 md:p-4 flex items-center justify-center">
                    <span className="text-white font-bold text-base sm:text-xl md:text-3xl">±0.5yd</span>
                  </div>
                  <div className="flex-1 h-14 sm:h-20 md:h-28 bg-white/10 backdrop-blur-sm rounded-lg md:rounded-2xl p-2 md:p-4 flex items-center justify-center">
                    <span className="text-white font-bold text-sm sm:text-lg md:text-3xl">AI Powered</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Phone Image 1 - My Golf Bag */}
            <div 
              className="col-span-1 md:col-span-3 group relative overflow-hidden bg-black cursor-pointer"
              onClick={() => handleFeatureClick('/my-golf-bag')}
            >
              <img 
                src="/Images/pexels-kampus-6542389.jpg" 
                alt="ClubIQ App"
                className="w-full h-full object-cover group-hover:scale-105 transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent group-hover:from-black/70 transition-all duration-300"></div>
              <div className="absolute bottom-4 left-4 right-4">
                <h4 className="text-white font-bold text-base md:text-xl group-hover:text-green-400 transition-colors">My Golf Bag</h4>
                <p className="text-white/80 text-xs md:text-sm mt-1">See gapping and overlaps so you know which clubs to keep, bench, or replace.</p>
              </div>
            </div>
            
            {/* Phone Image 2 - Shot Advisor */}
            <div 
              className="col-span-1 md:col-span-3 group relative overflow-hidden bg-black cursor-pointer"
              onClick={() => handleFeatureClick('/my-golf-bag')}
            >
              <img 
                src="/Images/pexels-markusspiske-114972.jpg" 
                alt="Golf Equipment"
                className="w-full h-full object-cover group-hover:scale-105 transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent group-hover:from-black/70 transition-all duration-300"></div>
              <div className="absolute bottom-4 left-4 right-4">
                <h4 className="text-white font-bold text-base md:text-xl group-hover:text-green-400 transition-colors">Shot Advisor</h4>
                <p className="text-white/80 text-xs md:text-sm mt-1">Club + target recommendations based on YOUR data, not stock yardage charts.</p>
              </div>
            </div>

            {/* Phone Image 3 - Swing Tips */}
            <div 
              className="col-span-1 md:col-span-3 group relative overflow-hidden bg-black cursor-pointer"
              onClick={() => handleFeatureClick('/club-optimizer?tab=swing-tips')}
            >
              <img 
                src="/Images/pexels-thomasleeward-2828723.jpg" 
                alt="Golf swing"
                className="w-full h-full object-cover group-hover:scale-105 transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent group-hover:from-black/70 transition-all duration-300"></div>
              <div className="absolute bottom-4 left-4 right-4">
                <h4 className="text-white font-bold text-base md:text-xl group-hover:text-green-400 transition-colors">Swing Tips</h4>
                <p className="text-white/80 text-xs md:text-sm mt-1">AI drills tailored to the misses your data shows.</p>
              </div>
            </div>

            {/* Phone Image 4 - Ball Matching */}
            <div 
              className="col-span-1 md:col-span-3 group relative overflow-hidden bg-black cursor-pointer"
              onClick={() => handleFeatureClick('/club-optimizer?tab=ball-match')}
            >
              <img 
                src="/Images/pexels-pixabay-274133.jpg" 
                alt="Golf Ball"
                className="w-full h-full object-cover group-hover:scale-105 transition-all duration-700"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent group-hover:from-black/70 transition-all duration-300"></div>
              <div className="absolute bottom-4 left-4 right-4">
                <h4 className="text-white font-bold text-base md:text-xl group-hover:text-green-400 transition-colors">Ball Matching</h4>
                <p className="text-white/80 text-xs md:text-sm mt-1">Match with balls that fit your swing speed and launch — not marketing claims.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* What is ClubIQ Section */}
      <div className="w-full bg-white py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-6">
          {/* Section Header */}
          <div className="text-center mb-12">
            <div className="inline-block px-4 py-2 bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-700 rounded-full text-sm font-bold uppercase tracking-wider mb-4">
              What You Get
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              What is ClubIQ?
            </h2>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto">
              ClubIQ is your free AI golf assistant that helps you understand your real distances, 
              optimize your bag, and make smarter decisions on every shot.
            </p>
          </div>

          {/* Value Props Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {/* Card 1 */}
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-100">
              <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center mb-4">
                <Target className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Know Your Real Yardages</h3>
              <p className="text-gray-600">
                Stop guessing between clubs. ClubIQ learns YOUR actual distances — not stock yardages 
                from a fitting chart. Know exactly how far you hit each club in your bag.
              </p>
            </div>

            {/* Card 2 */}
            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100">
              <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center mb-4">
                <BarChart3 className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Find Gaps in Your Bag</h3>
              <p className="text-gray-600">
                Discover if you're carrying two clubs that go the same distance, or missing a key 
                yardage. ClubIQ instantly shows you what's working and what's wasting space.
              </p>
            </div>

            {/* Card 3 */}
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6 border border-purple-100">
              <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center mb-4">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Get Smarter Recommendations</h3>
              <p className="text-gray-600">
                Our AI analyzes your data to suggest the right club for every shot, which clubs 
                to consider upgrading, and what changes would actually improve your game.
              </p>
            </div>

            {/* Card 4 */}
            <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl p-6 border border-orange-100">
              <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center mb-4">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">60-Second Setup</h3>
              <p className="text-gray-600">
                Answer a few quick questions, add your clubs, and you're done. No complicated 
                setup, no hardware required, no long forms. Start getting value immediately.
              </p>
            </div>

            {/* Card 5 */}
            <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-2xl p-6 border border-teal-100">
              <div className="w-12 h-12 bg-teal-500 rounded-xl flex items-center justify-center mb-4">
                <Cloud className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Weather-Adjusted Distances</h3>
              <p className="text-gray-600">
                Playing into the wind? Elevation change? ClubIQ factors in real-time conditions 
                so your yardages stay accurate no matter the weather or altitude.
              </p>
            </div>

            {/* Card 6 */}
            <div className="bg-gradient-to-br from-rose-50 to-red-50 rounded-2xl p-6 border border-rose-100">
              <div className="w-12 h-12 bg-rose-500 rounded-xl flex items-center justify-center mb-4">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Actually Lower Your Scores</h3>
              <p className="text-gray-600">
                This isn't about swing tips or expensive lessons. It's about making smarter 
                decisions with the game you already have. Most users see improvement in their first month.
              </p>
            </div>
          </div>

          {/* Bottom CTA */}
          <div className="text-center">
            <p className="text-gray-500 mb-4">Ready to see what ClubIQ can do for your game?</p>
            <button
              onClick={() => onOpenAuthModal("signup")}
              className="inline-flex items-center px-8 py-3 bg-green-600 text-white rounded-xl text-lg font-semibold hover:bg-green-700 transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Create Free Account
              <ArrowRight className="w-5 h-5 ml-2" />
            </button>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="w-full bg-gray-50 py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-6">
          {/* Section Header */}
          <div className="text-center mb-12">
            <div className="inline-block px-4 py-2 bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 rounded-full text-sm font-bold uppercase tracking-wider mb-4">
              Common Questions
            </div>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900">
              Frequently Asked Questions
            </h2>
          </div>

          {/* FAQ Items */}
          <div className="space-y-4">
            {[
              {
                question: "What exactly is ClubIQ?",
                answer: "ClubIQ is a free golf intelligence app that helps you understand your real club distances, find gaps in your bag, and make smarter club selections on the course. Think of it as your personal AI Club Fitter and Caddie that learns your game and helps you play better — without buying new equipment or expensive launch monitors."
              },
              {
                question: "How is this different from a rangefinder or GPS app?",
                answer: "Rangefinders tell you how far the flag is. ClubIQ tells you which club to hit to GET there based on YOUR actual distances — not stock yardages. We account for your swing, your clubs, and your tendencies. That's the difference between knowing '156 yards to pin' and knowing 'this is a smooth 7-iron for YOU.'"
              },
              {
                question: "Do I need a launch monitor or special hardware?",
                answer: "No! ClubIQ works with just your phone. You can manually log shots at the range, use our AI distance estimates based on your profile (handicap, swing speed, ball flight), or import data if you have it. Most golfers get accurate enough distances within their first range session."
              },
              {
                question: "How does ClubIQ know my distances?",
                answer: "When you sign up, we ask a few questions about your game — handicap, typical 7-iron distance, swing speed range, ball flight tendency. Our AI uses this to estimate all your club distances. Then you can refine them by logging actual shots. The more you use it, the smarter it gets."
              },
              {
                question: "What if I don't know my swing speed?",
                answer: "That's okay — most golfers don't! We'll estimate it based on your age, handicap, and typical distances. You can always update it later if you get measured, but our estimates are usually close enough to give you useful recommendations."
              },
              {
                question: "Is this actually free? What's the catch?",
                answer: "Core features are genuinely free forever — bag tracking, AI distance calculations, gapping analysis, shot advisor, and swing tips. We may add premium features later (advanced analytics, coach tools), but everything you need to play smarter golf will always be free. We're golfers too, and we built this to help people play better."
              },
              {
                question: "How long does setup take?",
                answer: "About 60 seconds. Answer a few questions about your game, add your clubs (we have most major brands in our database), and you're ready to go. You can refine your distances over time, but you'll have useful data immediately."
              },
              {
                question: "Does it work during tournament play?",
                answer: "Yes! ClubIQ is conforming for tournament play under USGA and R&A rules. We don't measure slope, wind, or actual distances during your round — we simply help you understand YOUR club distances so you can make better decisions. It's no different than writing your yardages on a card."
              },
              {
                question: "What's 'gapping' and why does it matter?",
                answer: "Gapping is the distance between each club in your bag. Ideally, you want consistent 10-15 yard gaps so you have a club for every distance. Many golfers unknowingly carry clubs that go the same distance (wasting a slot) or have big gaps (leaving them between clubs). ClubIQ identifies these problems instantly."
              },
              {
                question: "Can ClubIQ help me decide what clubs to buy?",
                answer: "Absolutely. Once you've logged your distances, ClubIQ shows you exactly where your gaps are and suggests what loft or club type would fill them. Stop buying clubs based on marketing — buy based on what your bag actually needs."
              },
              {
                question: "Does it work on iOS and Android?",
                answer: "ClubIQ is a progressive web app that works on any device with a browser — iPhone, Android, tablet, or desktop. Just visit clubiq.golf and add it to your home screen for an app-like experience. No app store download required."
              },
              {
                question: "Is my data private?",
                answer: "Yes. Your golf data is yours. We don't sell your information or share it with third parties. We use industry-standard encryption and secure authentication through Supabase. You can delete your account and all data at any time."
              }
            ].map((faq, index) => (
              <details 
                key={index} 
                className="group bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-all duration-300"
              >
                <summary className="flex items-center justify-between p-6 cursor-pointer list-none">
                  <h3 className="text-lg font-semibold text-gray-900 pr-4">{faq.question}</h3>
                  <ChevronDown className="w-5 h-5 text-gray-500 group-open:rotate-180 transition-transform duration-300 flex-shrink-0" />
                </summary>
                <div className="px-6 pb-6 pt-0">
                  <p className="text-gray-600 leading-relaxed">{faq.answer}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </div>

      {/* Final CTA - Full Width, No Margins */}
      <div className="relative w-full bg-gradient-to-br from-green-600 via-emerald-600 to-green-700 py-20 overflow-hidden">
        {/* Animated background elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full mix-blend-overlay filter blur-3xl animate-blob"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-white/10 rounded-full mix-blend-overlay filter blur-3xl animate-blob animation-delay-2000"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-white/10 rounded-full mix-blend-overlay filter blur-3xl animate-blob animation-delay-4000"></div>
              </div>
              
        <div className="relative z-10 text-center">
          <h2 className="text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-bold text-white mb-6 leading-tight">
            Ready to<br />
            <span className="bg-gradient-to-r from-yellow-200 via-yellow-100 to-white bg-clip-text text-transparent">
              Transform Your Game?
            </span>
              </h2>
          <p className="text-xl md:text-2xl lg:text-3xl text-white/95 mb-12 font-medium">
            Join thousands of golfers playing smarter with ClubIQ
          </p>
              <button
            onClick={() => onOpenAuthModal("signup")}
            className="group relative px-10 py-4 bg-white text-green-600 rounded-xl text-lg font-bold hover:bg-green-50 transition-all duration-300 shadow-2xl hover:shadow-white/50 hover:scale-105"
          >
            <span className="relative z-10 flex items-center">
              Get Started Free
              <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </span>
              </button>
            </div>
          </div>
      
      {/* Footer */}
      <footer className="w-full bg-gray-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-12">
            {/* Brand Column */}
            <div className="md:col-span-1">
              <div className="flex items-center gap-3 mb-4">
                <img 
                  src="/Images/New Logo Image.png" 
                  alt="ClubIQ Logo" 
                  className="h-10 w-auto rounded-lg"
                />
                <span className="text-xl font-bold">ClubIQ</span>
              </div>
              <p className="text-gray-400 text-sm leading-relaxed">
                The intelligence behind better golf. AI-powered precision for every shot.
              </p>
          </div>

            {/* Product Column */}
          <div>
              <h3 className="font-bold text-lg mb-4">Product</h3>
              <ul className="space-y-3">
                <li><a href="/my-golf-bag" className="text-gray-400 hover:text-white transition-colors text-sm">My Golf Bag</a></li>
                <li><a href="/club-optimizer" className="text-gray-400 hover:text-white transition-colors text-sm">Club Optimizer</a></li>
                <li><a href="/club-optimizer?tab=swing-tips" className="text-gray-400 hover:text-white transition-colors text-sm">Swing Tips</a></li>
                <li><a href="/club-optimizer?tab=ball-match" className="text-gray-400 hover:text-white transition-colors text-sm">Ball Matching</a></li>
              </ul>
          </div>

            {/* Company Column */}
          <div>
              <h3 className="font-bold text-lg mb-4">Company</h3>
              <ul className="space-y-3">
                <li><a href="/about" className="text-gray-400 hover:text-white transition-colors text-sm">About Us</a></li>
                <li><a href="/contact" className="text-gray-400 hover:text-white transition-colors text-sm">Contact</a></li>
                <li><a href="/privacy" className="text-gray-400 hover:text-white transition-colors text-sm">Privacy</a></li>
                <li><a href="/terms" className="text-gray-400 hover:text-white transition-colors text-sm">Terms</a></li>
              </ul>
          </div>

            {/* Social Column */}
          <div>
              <h3 className="font-bold text-lg mb-4">Connect</h3>
              {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
              <div className="flex gap-4">
                <button type="button" className="w-10 h-10 bg-gray-800 hover:bg-gray-700 rounded-lg flex items-center justify-center transition-colors" aria-label="Twitter">
                  <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                </button>
                <button type="button" className="w-10 h-10 bg-gray-800 hover:bg-gray-700 rounded-lg flex items-center justify-center transition-colors" aria-label="Instagram">
                  <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg>
                </button>
                <button type="button" className="w-10 h-10 bg-gray-800 hover:bg-gray-700 rounded-lg flex items-center justify-center transition-colors" aria-label="Facebook">
                  <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                </button>
                <button type="button" className="w-10 h-10 bg-gray-800 hover:bg-gray-700 rounded-lg flex items-center justify-center transition-colors" aria-label="LinkedIn">
                  <svg className="w-5 h-5 text-gray-400" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                </button>
              </div>
          </div>
        </div>

          {/* Bottom Bar */}
          <div className="border-t border-gray-800 pt-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-gray-400 text-sm">
                © 2024 ClubIQ. All rights reserved.
              </p>
              <div className="flex gap-6">
                <a href="/privacy" className="text-gray-400 hover:text-white transition-colors text-sm">Privacy Policy</a>
                <a href="/terms" className="text-gray-400 hover:text-white transition-colors text-sm">Terms of Service</a>
        </div>
      </div>
          </div>
        </div>
      </footer>
    </div>
    </>
  );
};

export default Dashboard;
